import {Vector2} from "@utility/vector2";
import {Game} from "@game/game";
import {Hand} from "@game/player/hand";
import React, {CSSProperties, PointerEventHandler, ReactElement, useCallback, useEffect, useRef, useState} from "react";
import {Bounds2} from "@utility/bounds2";
import {TileSlot} from "@components/interface/TileSlot";
import {TileButton} from "@components/interface/TileButton";
import {ShovelIcon} from "@components/interface/ShovelIcon";
import {THEME, TILE_BORDER, TILE_SIZE} from "@components/core/theme";
import {GameEvent} from "@game/gameEvent";

const BAR_BORDER = new Vector2(4, 4);

const styles: Record<string, CSSProperties> = {
    content: {
        position: "fixed",
        backgroundColor: "#3d2b1f",
        display: "flex",
        flexDirection: "row",
        left: 0,
        marginTop: BAR_BORDER.y,
        right: 0,
        bottom: 0
    },
    insert: {
        border: "black solid 1px",
        position: "fixed",
        left: 0,
        right: 0,
        height: TILE_SIZE.y,
        width: TILE_SIZE.x + BAR_BORDER.x * 2
    },
    insertHover: {
        boxShadow: "0 0 8px white",
        backgroundColor: "white",
        borderRadius: 4,
        margin: 8,
        padding: 2
    },
    shovelButton: {
        alignItems: "center",
        display: "flex",
        height: "100%",
        justifyContent: "flex-start",
        position: "relative",
        width: "100%"
    },
    shovelIcon: {
        height: "90%",
        width: "70%",
        flexShrink: 0
    },
    shovelText: {
        color: "#3d4a2f",
        fontFamily: "Georgia, 'Palatino Linotype', serif",
        fontWeight: "bold",
        fontSize: 16,
        position: "absolute",
        bottom: 16,
        right: 4,
        userSelect: "none" as const
    },
    slot: {
        maxHeight: TILE_SIZE.y + TILE_BORDER * 2,
        maxWidth: TILE_SIZE.x + TILE_BORDER * 2
    },
    slots: {
        alignContent: "flex-start",
        display: "flex",
        flexDirection: "row",
        flexGrow: 1,
        flexWrap: "wrap",
        overflowY: "auto",
        touchAction: "none"
    },
    tools: {
        display: "flex",
        flexDirection: "row",
        flexGrow: 1,
        margin: 2
    }
};

interface IBottomBar {
    dragPosition: Vector2
    game: Game
    hand: Hand
    onActive: () => void
    onBarBounds: (bounds: Bounds2) => void
    onShovel: () => void
    setInsert: (insert: number) => void
    shovelling: boolean
}
export function BottomBar({dragPosition, game, hand, onActive, onBarBounds, onShovel, setInsert, shovelling}: IBottomBar) {
    const [bounds, setBounds] = useState(Bounds2.zeroEmpty);
    const [, gameState] = useState<{}>();
    const forceUpdate = useCallback(() => gameState({}), []);
    const refContainer = useRef<HTMLDivElement>(null);
    const handleCancel = () => {
        game.userPlayer?.resetPlacedLetters();
    }
    const handleScore = () => {
        game.getWordsScore().catch(console.error);
    };
    const handleGo = () => {
        game.userPlayer?.placeLetters().catch(console.error);
    }
    const handlePointerDown: PointerEventHandler = (event) => {
        event.preventDefault();
    }

    const legal = !shovelling && hand.placed.length > 0 && game.placedIllegal.length === 0

    useEffect(() => {
        if (! bounds.contains(dragPosition)) {
            setInsert(-1);
        }
    }, [bounds, dragPosition]);
    useEffect(() => {
        const updateBoundingBox = () => {
            const bounds = refContainer.current!.getBoundingClientRect();
            const letterOffset = TILE_SIZE.y + 4;
            const size = Math.min(hand.held.length,  Math.floor(bounds.height / letterOffset));
            const slots = new Array(Math.min(size, hand.held.length)).fill(null)

            const b = new Bounds2(bounds);
            setBounds(b);
            onBarBounds(b);
            hand.held.forEach((_, index) => {
                slots[index] = index;
            });
        };

        if (refContainer.current) {
            updateBoundingBox();
            window.addEventListener("resize", updateBoundingBox);
        }
        return () => window.removeEventListener("resize", updateBoundingBox);
    }, [hand.held, refContainer.current]);
    useEffect(() => {
        const handleUpdate = () => {
            forceUpdate();
        };

        hand.events.delegates.add(handleUpdate);
        return () => hand.events.delegates.remove(handleUpdate);
    }, [hand]);
    useEffect(() => {
        const handleUpdate = (event: GameEvent) => {
            if (event.type === "ShovelledTile") {
                forceUpdate();
            }
        };

        game.events.delegates.add(handleUpdate);
        return () => game.events.delegates.remove(handleUpdate);
    }, [game.userPlayer]);
    return (
        <div className="bottom-bar-safe" data-draggable="bottombar" onPointerDown={handlePointerDown} ref={refContainer} style={styles.content}>
            <div style={styles.slots}>
                {hand.held.map((letter, index) =>
                    <div data-hand-slot={index} key={index} style={styles.slot}>
                        <TileSlot dragPosition={dragPosition} enabled={!shovelling} hand={hand} letter={letter ?? null}
                                  onDrag={onActive} setInsert={setInsert}/>
                    </div>
                )}
                <div style={styles.tools}>
                    <div style={{flexGrow: 1}}/>
                    <TileButton enabled={hand.shovels > 0} onClick={onShovel}>
                        <div style={styles.shovelButton}>
                            <ShovelIcon style={styles.shovelIcon}/>
                            <span style={styles.shovelText}>{`${hand.shovels}`}</span>
                        </div>
                    </TileButton>
                    <TileButton enabled={!shovelling && hand.placed.length > 0} onClick={handleCancel}
                                text={"ButtonCancel"}/>
                    <TileButton enabled={legal} onClick={handleScore} text={"ButtonScore"}/>
                    <TileButton enabled={legal} onClick={handleGo} text={"ButtonGo"}/>
                </div>
            </div>
        </div>
    );
}